import * as React from "react";

import { CandyGlimmer } from "./CandyGlimmer";
import { HexBytes } from "./HexBytes";
import AdhityaaProfilePicture from "../../images/adtac_profile_picture.jpg";

export const Post_250530_HTTP2Preface = () => {
  React.useEffect(() => {
    const orig = document.title;
    document.title = "Every byte explained: HTTP/2 connection preface and settings handshake | Subtrace";
    return () => {
      document.title = orig;
    };
  }, []);

  const bytes = new Uint8Array([
    0x50, 0x52, 0x49, 0x20, 0x2a, 0x20, 0x48, 0x54, 0x54, 0x50, 0x2f, 0x32, 0x2e, 0x30, 0x0d, 0x0a, 0x0d, 0x0a, 0x53, 0x4d, 0x0d, 0x0a, 0x0d, 0x0a, 0x00, 0x00, 0x12, 0x04, 0x00,
    0x00, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0xff, 0xff, 0x00, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00, 0x04, 0x00, 0x10, 0x00, 0x00,
  ]);

  const [visible, setVisible] = React.useState<number[]>([]);

  const sections: {
    ref: React.MutableRefObject<HTMLDivElement | null>;
    start: number;
    length: number;
  }[] = [
    { ref: React.useRef<HTMLDivElement | null>(null), start: 0, length: 24 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 24, length: 27 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 24, length: 8 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 32, length: 18 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 32, length: 6 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 38, length: 6 },
    { ref: React.useRef<HTMLDivElement | null>(null), start: 44, length: 6 },
  ];

  for (let idx = 0; idx < sections.length; idx++) {
    const { ref } = sections[idx];

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          for (const { isIntersecting } of entries) {
            if (!isIntersecting) {
              setVisible((prev) => {
                // console.log(`sentinel[${idx}]: going out: visibility ${prev.includes(idx)} -> false`);
                return [...prev.filter((x) => x !== idx)];
              });
            } else {
              setVisible((prev) => {
                // console.log(`sentinel[${idx}]: coming in: visibility ${prev.includes(idx)} -> true`);
                return [...prev.filter((x) => x !== idx), idx];
              });
            }
          }
        },
        { root: null, rootMargin: "-128px 0px 0px 0px", threshold: 0 },
      );
      io.observe(el);
      return () => io.disconnect();
    }, [ref, idx]);
  }

  return (
    <article className="w-full flex flex-col items-start px-[8px] sm:px-[36px] md:px-[72px] pt-12 sm:pt-24 pb-16 text-zinc-300/90">
      <h2 className="w-full flex flex-col space-y-3">
        <CandyGlimmer text={"// every byte explained"} />
        <span className="leading-[1.50] font-bold max-w-[21rem] xl:max-w-[24rem] text-[24px] xl:text-[28px] text-zinc-300">HTTP/2 connection preface and settings handshake</span>
      </h2>

      <div className="w-full h-[24px]" />

      <a href="https://x.com/adtac_" target="_blank" className="text-[12px] text-zinc-700 hover:text-zinc-600 font-medium flex items-center space-x-3 hover:brightness-[1.10]">
        <img src={AdhityaaProfilePicture} className="w-[24px] h-[24px] rounded-sm" />
        <div className="flex flex-col space-y-1 leading-[1]">
          <div>Adhityaa Chandrasekar</div>
          <div className="text-zinc-700/50">May 30, 2025</div>
        </div>
      </a>

      <div className="w-full h-[48px]" />

      <div className="w-full flex flex-col lg:flex-row-reverse relative">
        <HexBytes className="z-[1]" bytes={bytes} highlight={visible.length === 0 ? undefined : sections[visible.sort()[0]]} />
        <div className="z-[0] lg:max-w-[450px] mt-16 lg:mt-0 lg:mr-4 xl:mr-8 flex flex-col text-[16px] leading-[1.65]">
          <div className="w-full text-zinc-600 w-fit text-zinc-500/60 text-[13px] font-medium sm:max-w-[26rem]">
            Subtrace is a zero‑code, zero‑root network tracer that just works. We volunteer as tribute to read raw HTTP/2 hex so that you don't have to, but if you’re the
            zoom-all-the-way-in type, look no further.
          </div>

          <div className="w-full h-[48px]" />

          <div ref={sections[0].ref} className="flex flex-col">
            <h3 className="text-[21px] font-semibold flex text-zinc-300">
              <span>The preface</span>
            </h3>

            <div className="w-full h-[24px]" />

            <p>
              <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[8px] py-[4px] rounded-md font-mono text-[11px]">
                "PRI * HTTP/2.0\r\n\r\nSM\r\n\r\n"
              </pre>
            </p>

            <div className="w-full h-[16px]" />

            <p>
              HTTP/2 connections always start with this fixed 24-byte magic ritual called a <i>preface</i>.
            </p>

            <div className="w-full h-[16px]" />

            <p>
              The goal is to send bytes that look <i>kinda</i> like HTTP/1.1 but are certain to be invalid so that we can deal with proxies and middleboxes stuck in a HTTP/1.1
              worldview.
            </p>

            <div className="w-full h-[16px]" />

            <p>The idea is that these old servers would parse it as a PRI request, which isn't a valid HTTP method, so they will drop the connection rather than mangle it.</p>

            <div className="w-full h-[32px]" />

            <p className="bg-zinc-900/50 rounded-lg border border-zinc-900 px-[32px] py-[20px] text-[13px] leading-[1.80] text-zinc-500/60">
              <span className="font-medium text-zinc-400/60">Fun fact:</span>{" "}
              <span>
                Since there are infinitely many ways to construct invalid HTTP/1.1-looking requests, the preface went through a bikeshedding decathlon in earlier drafts. The
                original proposal was
              </span>{" "}
              <span className="font-medium text-zinc-400/60 w-fit px-[4px] font-mono text-[10px]">FOO..BAR</span> <span>but then it quickly</span>{" "}
              <a target="_blank" rel="noreferrer nofollow" href="https://github.com/httpwg/http2-spec/commit/a2a64d6272ce35f8c83c18d19638aa346501e77c">
                became
              </a>{" "}
              <span className="font-medium text-zinc-400/60 w-fit px-[4px] font-mono text-[10px]">FOO..BA</span> <span>to shave a byte. Two weeks later, it was</span>{" "}
              <a target="_blank" rel="noreferrer nofollow" href="https://github.com/httpwg/http2-spec/commit/79450fa6c7ad1530c2cfe7f2458fb02197c679b3">
                changed
              </a>{" "}
              <span>to</span> <span className="font-medium text-zinc-400/60 w-fit px-[4px] font-mono text-[10px]">STA..RT</span>{" "}
              <span>but that too lasted a mere 27 hours before</span>{" "}
              <a target="_blank" rel="noreferrer nofollow" href="https://github.com/httpwg/http2-spec/commit/ac468f3fab9f7092a430eedfd69ee1fb2e23c944">
                we got
              </a>{" "}
              <span className="font-medium text-zinc-400/60 w-fit px-[4px] font-mono text-[10px]">PRI..SM</span> finally.
            </p>

            <div className="w-full h-[32px]" />

            <p>
              Everything else in HTTP/2 is binary but the preface is pure plaintext. This way, endpoints can prove to each other they speak a specific binary protocol without
              leaking any binary so as to not upset any proxies in the middle.
            </p>
          </div>

          <div ref={sections[1].ref} className="flex flex-col pt-[96px]">
            <h3 className="text-[21px] font-semibold flex text-zinc-300">
              <span>The SETTINGS frame</span>
            </h3>

            <div className="w-full h-[24px]" />

            <p>
              After the preface, everything in HTTP/2 happens in <i>frames</i>.
            </p>

            <div className="w-full h-[16px]" />

            <p>The client sends a SETTINGS frame to set the parameters of the connection. In this example, everything after offset 24 is that frame.</p>

            <div className="w-full h-[16px]" />

            <p>Let's break it down.</p>
          </div>

          <div ref={sections[2].ref} className="flex flex-col pt-[96px]">
            <h4 className="text-[18px] font-medium flex text-zinc-300">
              <span>The header</span>
            </h4>

            <div className="w-full h-[16px]" />

            <p>
              The first eight bytes of every frame is the <i>header</i>.
            </p>

            <div className="w-full h-[24px]" />

            <div className="flex flex-col pl-4 opacity-[0.80]">
              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 00 12</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>3 bytes to indicate the frame size. This is just the payload size and does not include the size of the header itself.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">04</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>1 byte to indicate the frame type. There are twelve different frame types.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>1 byte for frame-specific flags. When the server acknowledges the client's settings, it will flip the ACK bit.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 00 00</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>
                  3 bytes to indicate the stream ID. HTTP/2 can multiplex many streams in a single connection and streams are identified by this ID. Zero means this is a
                  connection-level frame.
                </span>
              </p>
            </div>
          </div>

          <div ref={sections[3].ref} className="flex flex-col pt-[96px]">
            <h4 className="text-[18px] font-medium flex text-zinc-300">
              <span>The payload</span>
            </h4>

            <div className="w-full h-[16px]" />

            <p>
              The rest of the frame is the <i>payload</i>.
            </p>

            <div className="w-full h-[16px]" />

            <p>The meaning of its content depends on the frame type. It can be empty sometimes, but it's 18 bytes here.</p>

            <div className="w-full h-[16px]" />

            <p>
              SETTINGS frame payloads are always in six byte multiples. Each six byte chunk is a key-value <i>parameter</i>.
            </p>

            <div className="w-full h-[16px]" />

            <p>Let's look at each parameter.</p>
          </div>

          <div ref={sections[4].ref} className="pl-4 flex flex-col pt-[96px] lg:pt-[60px]">
            <div className="flex flex-col pl-8 opacity-[0.80] relative">
              <div className="opacity-[0.70] select-none absolute top-[-5px] left-[-16px] w-[32px] h-[32px] flex justify-center items-center bg-zinc-900 rounded-lg">
                <span className="text-zinc-700/80 font-medium text-[15px] p-2">1</span>
              </div>

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 03</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>Indicates this is the</span> <span className="font-mono text-[10px] text-zinc-200">MAX_CONCURRENT_STREAMS</span> <span>parameter.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 00 ff ff</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>The client wants the maximum number of concurrent streams in the connection set to 65,535, which is practically infinite.</span>
              </p>
            </div>
          </div>

          <div ref={sections[5].ref} className="pl-4 flex flex-col pt-[96px] lg:pt-[60px]">
            <div className="flex flex-col pl-8 opacity-[0.80] relative">
              <div className="opacity-[0.70] select-none absolute top-[-5px] left-[-16px] w-[32px] h-[32px] flex justify-center items-center bg-zinc-900 rounded-lg">
                <span className="text-zinc-700/80 font-medium text-[15px] p-2">2</span>
              </div>

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 02</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>Indicate this is the</span> <span className="font-mono text-[10px] text-zinc-200">ENABLE_PUSH</span> <span>parameter.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 00 00 00</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>The client wants to disable server push but these most CDNs have disabled server push on their end anyway because it wasn't worth it at scale.</span>
              </p>
            </div>
          </div>

          <div ref={sections[6].ref} className="pl-4 flex flex-col pt-[96px] lg:pt-[60px]">
            <div className="flex flex-col pl-8 opacity-[0.80] relative">
              <div className="opacity-[0.70] select-none absolute top-[-5px] left-[-16px] w-[32px] h-[32px] flex justify-center items-center bg-zinc-900 rounded-lg">
                <span className="text-zinc-700/80 font-medium text-[15px] p-2">3</span>
              </div>

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 04</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>Indicate this is the</span> <span className="font-mono text-[10px] text-zinc-200">INITIAL_WINDOW_SIZE</span> <span>parameter.</span>
              </p>

              <div className="w-full h-[24px]" />

              <p>
                <pre className="bg-zinc-800/50 outline outline-[1px] outline-zinc-800/80 w-fit px-[6px] py-[3px] rounded-md font-mono text-[10px]">00 10 00 00</pre>
              </p>

              <div className="w-full h-[8px]" />

              <p className="pl-4 text-[14px]">
                <span>The client wants 16x the default window size, which is great for long, fat data pipes but can also cause flow control stalls sometimes.</span>
              </p>
            </div>

            <div className="flex flex-col pt-[96px]">
              <h4 className="text-[18px] font-medium flex text-zinc-300">
                <span>Next up</span>
              </h4>

              <div className="w-full h-[16px]" />

              <p>Next week, we'll vivisect HPACK compression and watch header entropy collapse. Bring popcorn.</p>
            </div>

            <div className="w-full h-[96px]" />
          </div>
        </div>
      </div>
    </article>
  );
};
